import React, { useState, useEffect } from 'react';
import axios from 'axios';
import './profiles.css';
import './home-style.css';
import Modal from './Modal';

const CategoriesPage = ({ setIsAnyModalActive }) => {
    const [showAddCategoryModal, setShowAddCategoryModal] = useState(false);
    const [showEditModal, setShowEditModal] = useState(false);
    const [categoryName, setCategoryName] = useState('');
    const [editCategoryId, setEditCategoryId] = useState('');
    const [editCategoryName, setEditCategoryName] = useState('');
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [categoryToDelete, setCategoryToDelete] = useState(null);
    const [categories, setCategories] = useState([]);
    const [showSuccessMessage, setShowSuccessMessage] = useState(false);
    const [successMessage, setSuccessMessage] = useState('');
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [error, setError] = useState('');
    const [originalCategoryName, setOriginalCategoryName] = useState('');
    const [categoryType, setCategoryType] = useState("main");
    const [parentCategory, setParentCategory] = useState(null);
    const [editCategoryType, setEditCategoryType] = useState('');
    const [originalCategoryType, setOriginalCategoryType] = useState('');
    const [editParentCategory, setEditParentCategory] = useState('');
    const [originalParentCategory, setOriginalParentCategory] = useState('');


    const fetchCategories = async () => {
        setIsSubmitting(true);
        try {
            const response = await fetch('https://admin-new.lasgidirestaurant.com/php/categories.php');
            const data = await response.json();
            setTimeout(() => {
                setCategories(data);
                setIsSubmitting(false);
            }, 500);
        } catch (error) {
            setIsSubmitting(false);
        }
    };

    

    useEffect(() => {
        fetchCategories();
    }, []);

   

    const handleAddCategoryClick = () => {
        setIsAnyModalActive(true);
        setShowAddCategoryModal(true);
    };

    const handleCloseModal = () => {
        setIsAnyModalActive(false);
        setShowAddCategoryModal(false);
        setShowDeleteModal(false);
        resetFormState();
    };

    const resetFormState = () => {
        setCategoryName('');
        setErrorWithTimeout('');
        setCategoryType('main');
        setParentCategory(null);
    };

    const handleFormSubmit = async (e) => {
        e.preventDefault();

        setErrorWithTimeout('');
        setIsSubmitting(true);

        try {
            const response = await axios.post('https://admin-new.lasgidirestaurant.com/php/categories.php', {
                categoryName,
                categoryType,
                parentCategory: categoryType === "sub" ? parentCategory : null,
            });

            if (response.data.status === 'error' && response.data.message === 'Category already exists') {
                setErrorWithTimeout('Category already exists');
            } else {
                setTimeout(() => {
                    setIsSubmitting(false);
                    handleCloseModal();
                    fetchCategories();
                    showSuccess('Category added successfully');
                }, 500);
            }
        } catch (error) {
            setIsSubmitting(false);

            if (error.response) {
                if (error.response.status === 409) {
                    setErrorWithTimeout('Category already exists');
                } else {
                    setErrorWithTimeout('Unexpected error occurred: ' + (error.response.data.message || ''));
                }
            } else {
                setErrorWithTimeout('Network or server error occurred.');
            }
        }
    };

    const handleEditModalClose = () => {
        setIsAnyModalActive(false);
        setShowEditModal(false);
        resetEditFormState();
    };

    const resetEditFormState = () => {
        setEditCategoryId('');
        setEditCategoryName('');
    };

    const handleUpdateCategory = async (e) => {
        e.preventDefault();

        setErrorWithTimeout('');
        setIsSubmitting(true);

        // Prevent update if nothing has changed
        const isUnchanged =
            editCategoryName === originalCategoryName &&
            editCategoryType === originalCategoryType &&
            editParentCategory === originalParentCategory;

        if (isUnchanged) {
            setIsSubmitting(false);
            handleEditModalClose();
            return;
        }

        try {
            // Ensure correct parent category handling
            const parentCategoryId = editCategoryType === "sub" ? editParentCategory : null;

            const payload = {
                id: editCategoryId,
                category_name: editCategoryName.trim(),
                category_type: editCategoryType,
                parent_id: parentCategoryId
            };

            const response = await axios.put(
                `https://admin-new.lasgidirestaurant.com/php/categories.php?id=${editCategoryId}`,
                payload
            );

            if (response.data.status === 'error') {
                setIsSubmitting(false);

                if (response.data.message === 'Category already exists') {
                    setErrorWithTimeout('Category already exists');
                } else {
                    setErrorWithTimeout(response.data.message || 'An error occurred.');
                }
                return;
            }

            // Success: Update the UI
            setTimeout(() => {
                setIsSubmitting(false);
                handleEditModalClose();
                fetchCategories(); // Refresh categories list
                showSuccess('Category updated successfully');
            }, 500);

        } catch (error) {
            setIsSubmitting(false);

            if (error.response) {
                const { status, data } = error.response;
                if (status === 409) {
                    setErrorWithTimeout('Category already exists');
                } else {
                    setErrorWithTimeout(data?.message || 'Unexpected error occurred.');
                }
            } else {
                setErrorWithTimeout('Network or server error occurred.');
            }
        }
    };

    const handleEditCategory = (category, allCategories = []) => {
        if (!allCategories || !Array.isArray(allCategories)) {
            return;
        }

        setEditCategoryId(category.id);
        setEditCategoryName(category.category_name); // Ensure the correct name is used
        setOriginalCategoryName(category.category_name);

        setIsAnyModalActive(true);
        setShowEditModal(true);

        // Check if this category is a subcategory
        const parentCategory = allCategories.find(parent =>
            parent.subcategories && parent.subcategories.some(sub => sub.id === category.id)
        );

        if (parentCategory) {
            // ✅ It's a subcategory
            setEditCategoryType("sub");
            setOriginalCategoryType("sub");
            setEditParentCategory(parentCategory.id); // Correctly assign parent category ID
            setOriginalParentCategory(parentCategory.id);
        } else {
            // ✅ It's a main category
            setEditCategoryType("main");
            setOriginalCategoryType("main");
            setEditParentCategory(null);
            setOriginalParentCategory(null);
        }
    };


    const handleDeleteCategory = (category) => {
        setCategoryToDelete(category);
        setIsAnyModalActive(true);
        setShowDeleteModal(true);
    };

    const confirmDeleteCategory = async (e) => {
        e.preventDefault();
        setIsSubmitting(true);

        try {
            await axios.delete(`https://admin-new.lasgidirestaurant.com/php/categories.php`, {
                data: { id: categoryToDelete.id }
            });

            setTimeout(() => {
                setIsSubmitting(false);
                setIsAnyModalActive(false);
                setShowDeleteModal(false);
                fetchCategories();
                showSuccess('Category deleted successfully');
            }, 500);
        } catch (error) {
            setIsSubmitting(false);

            if (error.response && error.response.data && error.response.data.message) {
                setErrorWithTimeout(error.response.data.message);
            } else {
                setErrorWithTimeout('An error occurred while deleting the category.');
            }

        }
    };


    const showSuccess = (message) => {
        setSuccessMessage(message);
        setShowSuccessMessage(true);
        setTimeout(() => setShowSuccessMessage(false), 3000);
    };

    const setErrorWithTimeout = (errorMessage) => {
        setError(errorMessage);
        if (errorMessage) {
            setTimeout(() => {
                setError('');
            }, 3000);
        }
    };

    // Prepare sorting logic
    const sortedCategories = [...categories].sort((a, b) => {
        const priority = {
            1: 1,   // Starters
            17: 2,  // Main Menu
            16: 99  // Drinks
        };

        const aPriority = priority[a.id] || 50;
        const bPriority = priority[b.id] || 50;

        return aPriority - bPriority;
    });
    
    return (
        <main className="profiles-main">
            {showSuccessMessage && <div className="success-message">{successMessage}</div>}
            <div className="profiles-container">
                {/* Main Content */}
                <div>
                    <div className="top-right">
                        <button className="profiles-btn add-btn add-category" onClick={handleAddCategoryClick}>
                            Add Category
                        </button>
                    </div>
                    <div className="categories-list">
                        {categories.length === 0 ? (
                            <div className="no-users-found">No categories found.</div>
                        ) : (
                            sortedCategories.map((category, index) => (
                                <div key={category.id} className="category-item">
                                    {/* Main Category */}
                                    <div className="user">
                                        <div className='user-info'>
                                            <span className="user-number">({index + 1})</span>
                                            <span className='person-name'>{category.category_name}</span>
                                        </div>
                                        <div className='action-buttons'>
                                            <button className="edit-btn" onClick={() => handleEditCategory(category, categories)}>
                                                <i className="fas fa-pencil-alt"></i>
                                            </button>
                                            <button className="delete-btn" onClick={() => handleDeleteCategory(category)}>
                                                <i className="fas fa-trash-alt"></i>
                                            </button>
                                        </div>
                                    </div>

                                    {/* Subcategories */}
                                    {category.subcategories.length > 0 && (
                                        <div className="subcategories-list">
                                            {category.subcategories.map((subCategory, subIndex) => (
                                                <div key={subCategory.id} className="subcategory-item user">
                                                    <div className='user-info subcategory'>
                                                        <span className="subcategory-number">({String.fromCharCode(97 + subIndex)})</span>
                                                        <span className='person-name'>{subCategory.category_name}</span>
                                                    </div>
                                                    <div className='action-buttons'>
                                                        <button className="edit-btn" onClick={() => handleEditCategory(subCategory, categories)}>
                                                            <i className="fas fa-pencil-alt"></i>
                                                        </button>
                                                        <button className="delete-btn" onClick={() => handleDeleteCategory(subCategory)}>
                                                            <i className="fas fa-trash-alt"></i>
                                                        </button>
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                    )}
                                </div>
                            ))
                        )}
                    </div>


                    {/* Add Category Modal */}
                    {showAddCategoryModal && (
                        <Modal
                            title="Add Category"
                            onClose={handleCloseModal}
                            onSubmit={handleFormSubmit}
                            submitButtonText="Add Category"
                            showCancel={false}
                            className="modal-content"
                            showSubmitButton={true}
                        >
                            <div className="profiles-form">
                                {/* Category Name Input */}
                                <div className="form-group">
                                    <input
                                        type="text"
                                        value={categoryName}
                                        placeholder='Enter Category Name'
                                        onChange={(e) => setCategoryName(e.target.value)}
                                        required
                                    />
                                </div>

                                {/* Select Category Type */}
                                <div className="category-form-group">
                                    <label>
                                        <input
                                            type="radio"
                                            value="main"
                                            checked={categoryType === "main"}
                                            onChange={() => {
                                                setCategoryType("main");
                                                setParentCategory(null);
                                            }}
                                        />
                                        Main Category
                                    </label>

                                    <label>
                                        <input
                                            type="radio"
                                            value="sub"
                                            checked={categoryType === "sub"}
                                            onChange={() => setCategoryType("sub")}
                                        />
                                        Sub Category
                                    </label>
                                </div>

                                {/* Parent Category Dropdown (Only if Subcategory is Selected) */}
                                {categoryType === "sub" && (
                                    <div className="form-group">
                                        <select value={parentCategory} onChange={(e) => setParentCategory(e.target.value)} required>
                                            <option value="">Select Parent Category</option>
                                            {categories.map((category) => (
                                                <option key={category.id} value={category.id}>{category.category_name}</option>
                                            ))}
                                        </select>
                                    </div>
                                )}
                            </div>
                        </Modal>
                    )}

                    {error && (
                        <div className="error-overlay">
                            <div className='error-overlay-container'>
                                <i className="fas fa-exclamation-triangle"></i>
                                <p className="error-overlay-message">{error}</p>
                            </div>
                        </div>
                    )}

                    {isSubmitting && (
                        <div className="spinner-overlay">
                            <div className="spinner"></div>
                        </div>
                    )}

                    {/* Edit Category Modal */}
                    {showEditModal && (
                        <Modal
                            title={'Edit Category'}
                            onClose={handleEditModalClose}
                            onSubmit={handleUpdateCategory}
                            submitButtonText={'Update'}
                            showCancel={false}
                            showSubmitButton={true}
                            className="modal-content"
                        >
                            <div className="profiles-form">
                                <div className="form-group">
                                    <input
                                        type="text"
                                        placeholder="Category Name"
                                        value={editCategoryName}
                                        onChange={(e) => setEditCategoryName(e.target.value)}
                                        required
                                    />
                                </div>

                                {/* Category Type Selection */}
                                <div className="category-form-group">
                                    <label>
                                        <input
                                            type="radio"
                                            value="main"
                                            checked={editCategoryType === "main"}
                                            onChange={() => {
                                                setEditCategoryType("main");
                                                setEditParentCategory(null);
                                            }}
                                        />
                                        Main Category
                                    </label>
                                    <label style={{ marginLeft: "10px" }}>
                                        <input
                                            type="radio"
                                            value="sub"
                                            checked={editCategoryType === "sub"}
                                            onChange={() => setEditCategoryType("sub")}
                                        />
                                        Sub Category
                                    </label>
                                </div>

                                {/* Parent Category Selection (Only for Subcategories) */}
                                {editCategoryType === "sub" && (
                                    <div className="form-group">
                                        <label>Select Parent Category:</label>
                                        <select
                                            value={editParentCategory || ""}
                                            onChange={(e) => setEditParentCategory(e.target.value)}
                                        >
                                            <option value="">-- Select Parent Category --</option>
                                            {categories.map((cat) => (
                                                <option key={cat.id} value={cat.id}>
                                                    {cat.category_name}
                                                </option>
                                            ))}
                                        </select>
                                    </div>
                                )}
                            </div>
                        </Modal>
                    )}

                    {/* Delete Category Modal */}
                    {showDeleteModal && (
                        <Modal
                            title="Delete Category"
                            onClose={handleCloseModal}
                            onSubmit={confirmDeleteCategory}
                            submitButtonText="Delete"
                            showCancel={true}
                            showSubmitButton={true}
                            className="modal-content delete-modal-content"
                        >
                            <div>
                                <p className='delete-modal-heading'>Are you sure you want to delete this category?</p>
                            </div>
                        </Modal>
                    )}

                </div>
            </div>
        </main >
    );
};

export default CategoriesPage;
